/**
 * フィルム × 現像液 の標準現像時間 DB（秒）
 *
 * 出典の目安:
 * - メーカー公式データシート
 * - Massive Dev Chart (https://www.digitaltruth.com/devchart.php) ※20°C, ISO 表記感度ベース
 *
 * N (normal) 時間のみ DB に格納。N±時間はランタイムで N に倍率を掛けて算出:
 *   N+1 = N × 1.40
 *   N+2 = N × 1.85
 *   N+3 = N × 2.30
 *   N-1 = N × 0.70
 *   N-2 = N × 0.50
 *   N-3 = N × 0.40
 *
 * 全て 20°C を前提とする。温度補正係数は別途 developer.js で扱う。
 *
 * 注: ユーザーは personal offset で実測値補正を上書き可能 (presets.js)。
 *     ここの値はあくまで初期値（出発点）として扱う。
 */

// [filmId][developerId] = 秒
export const DEV_TIMES_N = {
  'tri-x-400': {
    'd76-stock':   450,   // 7'30"
    'd76-1+1':     585,   // 9'45"
    'hc110-b':     330,   // 5'30"
    'hc110-h':     510,   // 8'30"
    'xtol-stock':  390,   // 6'30"
    'xtol-1+1':    540,   // 9'00"
    'tmax-dev':    390,
    'id11-stock':  450,
    'id11-1+1':    585,
    'ddx-1+4':     420,
    'rodinal-1+25':  300, // 5'00"
    'rodinal-1+50':  720, // 12'00"
    'rodinal-1+100':3600, // 60'00" stand
    'pyrocat-hd':  720,
    'pmk-pyro':    720,
    'caffenol-cm': 900,   // 15'00"
  },
  'tmax-100': {
    'd76-stock':   450,
    'd76-1+1':     600,
    'hc110-b':     330,
    'xtol-stock':  390,
    'xtol-1+1':    540,
    'tmax-dev':    420,
    'id11-stock':  450,
    'id11-1+1':    600,
    'ddx-1+4':     420,
    'pyrocat-hd':  900,
    'rodinal-1+50':780,
  },
  'tmax-400': {
    'd76-stock':   450,
    'd76-1+1':     750,   // 12'30"
    'hc110-b':     360,
    'xtol-stock':  420,
    'xtol-1+1':    570,   // 9'30"
    'tmax-dev':    390,
    'id11-stock':  450,
    'id11-1+1':    750,
    'ddx-1+4':     420,
    'rodinal-1+25':420,
    'rodinal-1+50':780,
    'pyrocat-hd':  900,
  },
  'tmax-3200': {
    'd76-stock':   780,
    'tmax-dev':    660,
    'xtol-stock':  600,
    'ddx-1+4':     540,
    'microphen':   540,
  },
  'hp5-plus': {
    'd76-stock':   450,
    'd76-1+1':     660,   // 11'00"
    'id11-stock':  450,
    'id11-1+1':    660,
    'hc110-b':     270,
    'xtol-stock':  390,
    'xtol-1+1':    540,
    'ddx-1+4':     480,   // 8'00"
    'rodinal-1+25':390,
    'rodinal-1+50':720,
    'pyrocat-hd':  720,
    'perceptol-stock': 660,
    'perceptol-1+1':   780,
    'microphen':   330,
    'caffenol-cm': 900,
  },
  'fp4-plus': {
    'd76-stock':   330,
    'd76-1+1':     510,
    'id11-stock':  330,
    'id11-1+1':    510,
    'hc110-b':     270,
    'xtol-stock':  330,
    'xtol-1+1':    480,
    'ddx-1+4':     360,
    'rodinal-1+25':300,
    'rodinal-1+50':600,
    'pyrocat-hd':  720,
    'perceptol-stock': 540,
    'caffenol-cm': 720,
  },
  'delta-100': {
    'd76-stock':   480,
    'd76-1+1':     660,
    'hc110-b':     330,
    'xtol-stock':  450,
    'xtol-1+1':    540,
    'ddx-1+4':     360,
    'pyrocat-hd':  900,
    'rodinal-1+25':420,
    'rodinal-1+50':720,
  },
  'delta-400': {
    'd76-stock':   510,
    'd76-1+1':     720,
    'hc110-b':     330,
    'xtol-stock':  480,
    'xtol-1+1':    600,
    'ddx-1+4':     480,
    'rodinal-1+25':420,
    'rodinal-1+50':780,
    'pyrocat-hd':  900,
  },
  'delta-3200': {
    'd76-stock':   660,
    'xtol-stock':  570,
    'ddx-1+4':     510,
    'microphen':   600,
  },
  'pan-f-plus': {
    'd76-stock':   270,
    'd76-1+1':     420,
    'hc110-b':     180,
    'id11-stock':  270,
    'id11-1+1':    420,
    'rodinal-1+25':240,
    'rodinal-1+50':480,
    'perceptol-1+1':600,
  },
  'sfx-200': {
    'd76-stock':   450,
    'id11-stock':  450,
    'hc110-b':     360,
    'rodinal-1+25':480,
  },
  'acros-100-ii': {
    'd76-stock':   360,
    'd76-1+1':     510,   // 8'30"
    'hc110-b':     270,
    'xtol-stock':  330,
    'xtol-1+1':    390,   // 6'30"
    'tmax-dev':    300,
    'rodinal-1+25':360,
    'rodinal-1+50':600,
    'pyrocat-hd':  600,
  },
  'neopan-400': {
    'd76-stock':   390,
    'd76-1+1':     570,
    'hc110-b':     330,
    'xtol-stock':  420,
  },
  'fomapan-100': {
    'd76-stock':   360,
    'd76-1+1':     480,
    'rodinal-1+25':300,
    'rodinal-1+50':600,
    'fomadon-r09-1+50': 540,
    'pyrocat-hd':  600,
  },
  'fomapan-400': {
    'd76-stock':   480,
    'd76-1+1':     660,
    'rodinal-1+25':420,
    'rodinal-1+50':720,
    'fomadon-r09-1+50': 660,
    'pyrocat-hd':  720,
  },
  'cms-20-ii': {
    'd76-stock':   720,   // 専用現像液 Adotech 推奨だが、汎用用に 12'
    'rodinal-1+100':2700,
  },
  'silvermax': {
    'd76-stock':   480,
    'rodinal-1+50':900,
  },
  'pancro-400': {
    'd76-stock':   540,
    'pyrocat-hd':  900,
    'rodinal-1+50':720,
  },
};

// N±倍率
export const N_MULTIPLIERS = {
  '+3': 2.30,
  '+2': 1.85,
  '+1': 1.40,
   '0': 1.00,
  '-1': 0.70,
  '-2': 0.50,
  '-3': 0.40,
};

/**
 * フィルム×現像液から N時間（秒）を取得
 */
export function getNTimeSeconds(filmId, developerId) {
  return DEV_TIMES_N[filmId]?.[developerId] ?? null;
}

/**
 * N時間に倍率を掛けて N±時間を算出
 * @param {number} nSec - N時間（秒）
 * @param {string} plan - 'N', 'N+1', 'N-1', 'N+2', 'N-2', ...
 */
export function applyNPlanToTime(nSec, plan) {
  if (nSec == null) return null;
  const m = plan.match(/^N([+-]?\d?)$/);
  const key = m ? (m[1] || '0') : '0';
  const mult = N_MULTIPLIERS[key] ?? 1.0;
  return Math.round(nSec * mult);
}

/**
 * 利用可能な現像液リスト（あるフィルムに対して）
 */
export function getAvailableDevelopersForFilm(filmId) {
  const times = DEV_TIMES_N[filmId];
  if (!times) return [];
  return Object.keys(times);
}

/**
 * 秒 → "M:SS" or "MM:SS"
 */
export function formatSeconds(sec) {
  if (sec == null || sec < 0) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
